import { useState } from 'react';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';

import type PaymentStore from 'src/stores/payment-store';
import popupStore from 'src/stores/popup-store';

import useSluggedNavigate from 'src/hooks/use-slugged-navigate';
import { AppRoute, ErrorText, LOCALES, PaymentType, PopupName } from 'src/constants';

interface Transaction {
  client_secret: string;
  no: string;
}

export default function useStripePayment(paymentStore: typeof PaymentStore) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useSluggedNavigate();
  const [storedOrder, setStoredOrder] = useState<Transaction | null>(null);

  const handlePaymentButtonClick = async () => {
    if (paymentStore.paymentFormError) {
      return;
    }

    if (!paymentStore.isFormComplete) {
      paymentStore.setPaymentFormError(ErrorText.FILL_FORM);
      return;
    }

    if (!stripe || !elements) {
      paymentStore.setPaymentFormError(ErrorText.DEFAULT);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (cardElement === null) {
      paymentStore.setPaymentFormError(ErrorText.DEFAULT);
      return;
    }

    const { client_secret, no } =
      storedOrder || (await paymentStore.createOrder(PaymentType.STRIPE));

    if (client_secret) {
      setStoredOrder({ client_secret, no });
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (paymentIntent) {
        paymentStore.setPaidOrderNumber(no);
        navigate(`${AppRoute.PAYMENT_RESULT_PAGE_ROUTE}/${no}`);
      } else if (error?.message) {
        paymentStore.setPaymentFormError(error.message);
        popupStore.showPopup(PopupName.INFO, {
          title: LOCALES.PAYMENT_ERROR,
          text: error.message,
        });
        paymentStore.setPaymentLoading(false);
      }
    }
  };

  return handlePaymentButtonClick;
}
